/**
 * ManageSigningModal — for a request that's out for signature:
 *  - resend someone their link (optionally to a corrected email address)
 *  - release people whose signature isn't needed, so the request carries on with the signatures it has
 *  - resend the executor's "sign your part" request
 */
import React, { useEffect, useState } from 'react';
import { Copy, Send, CheckCircle2, Clock, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { esignAPI } from '@/lib/api';
import { safeFormat } from '@/lib/utils';

const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const who = (p) => p.name || p.email;

export default function ManageSigningModal({ submission, onClose, onChanged }) {
  const [sub, setSub] = useState(submission);
  const [emails, setEmails] = useState({});
  const [release, setRelease] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null);
  const [execEmail, setExecEmail] = useState(submission.executor_email || submission.sent_by || '');

  useEffect(() => { setEmails(Object.fromEntries((sub.parties || []).map((p) => [p.id, p.email]))); }, [sub]);

  const refresh = async () => {
    try {
      const r = await esignAPI.listSubmissions();
      const fresh = (r.data || []).find((x) => x._id === sub._id);
      if (fresh) { setSub(fresh); setExecEmail(fresh.executor_email || fresh.sent_by || ''); }
    } catch { /* keep what we have */ }
    onChanged();
  };

  const pending = (sub.parties || []).filter((p) => p.status === 'pending');
  const done = (sub.parties || []).filter((p) => p.status === 'signed' || p.status === 'reviewed');
  const releasing = pending.filter((p) => release[p.id]);
  const stillPending = pending.filter((p) => !release[p.id]);
  const hasSenderFields = (sub.fields || []).some((f) => f.role === 'sender');
  const executor = sub.executor_name || sub.executor_email || sub.sent_by;
  const next = stillPending.length
    ? `It will keep waiting on ${stillPending.map(who).join(', ')}.`
    : hasSenderFields
      ? `${executor} will be emailed to sign their part, then everyone gets the final copy.`
      : 'It will be fully executed and everyone gets the final copy and certificate.';

  const resend = async (p) => {
    setBusy(p.id);
    try {
      const r = await esignAPI.resend(sub._id, { party_id: p.id, email: emails[p.id] !== p.email ? emails[p.id] : undefined });
      toast.success(`Sent to ${r.data.sent_to}`);
      await refresh();
    } catch (e) { err(e, 'Could not resend'); } finally { setBusy(null); }
  };

  const doRelease = async () => {
    if (!window.confirm(`Mark ${releasing.map(who).join(', ')} as not required? This is recorded in the audit trail. ${next}`)) return;
    setBusy('release');
    try {
      const r = await esignAPI.notRequired(sub._id, releasing.map((p) => p.id), note || undefined);
      toast.success(r.data.status === 'executed' ? 'Fully executed. The final copy is on its way to everyone.'
        : r.data.status === 'awaiting_execution' ? `Sent to ${executor} to sign their part` : 'Updated');
      setRelease({}); setNote('');
      await refresh();
    } catch (e) { err(e, 'Could not update'); } finally { setBusy(null); }
  };

  const resendExecutor = async () => {
    setBusy('exec');
    try {
      const r = await esignAPI.resend(sub._id, { executor: true, email: execEmail !== (sub.executor_email || sub.sent_by) ? execEmail : undefined });
      toast.success(`Sent to ${r.data.sent_to}`);
      await refresh();
    } catch (e) { err(e, 'Could not resend'); } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sub.document_title}</DialogTitle>
          <DialogDescription>
            {done.length} of {(sub.parties || []).filter((p) => p.status !== 'not_required').length} done. Resend a link, fix an email address, or mark people as not required if the signatures you have are enough.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {(sub.parties || []).map((p) => (
            <div key={p.id} className="rounded-lg border border-[#E3DDD1] p-3">
              <div className="flex items-center gap-2">
                {p.status === 'pending' ? <Clock className="w-4 h-4 text-amber-600" /> : p.status === 'not_required' ? <MinusCircle className="w-4 h-4 text-[#9AA3AB]" /> : <CheckCircle2 className="w-4 h-4 text-green-600" />}
                <span className={`text-sm font-medium flex-1 ${p.status === 'not_required' ? 'text-[#9AA3AB] line-through' : 'text-[#1E2A33]'}`}>
                  {who(p)} <span className="text-xs font-normal text-[#5C6670]">({p.role})</span>
                </span>
                <span className="text-xs text-[#5C6670]">
                  {p.status === 'pending' ? (p.viewed_at ? `Opened ${safeFormat(p.viewed_at, 'MMM d')}` : 'Not opened yet')
                    : p.status === 'not_required' ? 'Not required'
                      : `${p.role === 'reviewer' ? 'Reviewed' : 'Signed'} ${safeFormat(p.signed_at, 'MMM d, yyyy h:mm a')}`}
                </span>
              </div>
              {p.status === 'pending' && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Input className="h-8 text-sm flex-1 min-w-[200px]" value={emails[p.id] || ''} onChange={(e) => setEmails({ ...emails, [p.id]: e.target.value })} />
                  <Button size="sm" variant="outline" className="h-8" disabled={busy === p.id} onClick={() => resend(p)}>
                    <Send className="w-3.5 h-3.5 mr-1" />{emails[p.id] !== p.email ? 'Update and send' : 'Resend'}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8" title="Copy their signing link" onClick={() => { navigator.clipboard.writeText(p.signing_url); toast.success('Link copied'); }}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <label className="flex items-center gap-1.5 text-xs text-[#5C6670] ml-auto">
                    <input type="checkbox" checked={!!release[p.id]} onChange={(e) => setRelease({ ...release, [p.id]: e.target.checked })} />
                    Not required
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {releasing.length > 0 && (
          <div className="rounded-lg bg-[#FBF8F2] border border-[#E3DDD1] p-3 space-y-2">
            <p className="text-sm text-[#1E2A33]"><strong>{releasing.map(who).join(', ')}</strong> won't need to sign. {next}</p>
            <Input className="h-8 text-sm" placeholder="Reason for the audit trail (optional), e.g. Two Assist24 signatories are sufficient" value={note} onChange={(e) => setNote(e.target.value)} />
            <Button className="bg-[#A62B1F] hover:bg-[#8C2318] text-white" disabled={busy === 'release'} onClick={doRelease}>
              {busy === 'release' ? 'Updating…' : `Continue without ${releasing.length === 1 ? 'them' : `these ${releasing.length}`}`}
            </Button>
          </div>
        )}

        {hasSenderFields && (
          <div className="rounded-lg border border-[#E3DDD1] p-3 space-y-2">
            <div className="text-sm font-medium text-[#1E2A33]">Executor: {executor}</div>
            {sub.status === 'awaiting_execution' ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[#5C6670] w-full">Everyone else is done. Their "sign your part" email can be sent again, or to a different address.</span>
                <Input className="h-8 text-sm flex-1 min-w-[200px]" value={execEmail} onChange={(e) => setExecEmail(e.target.value)} />
                <Button size="sm" variant="outline" className="h-8" disabled={busy === 'exec'} onClick={resendExecutor}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>
              </div>
            ) : (
              <p className="text-xs text-[#5C6670]">They're asked to sign their part once everyone above has finished (or been marked not required).</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
